/**
 * Searchable producer dropdown component.
 *
 * Manages the DOM for a filterable dropdown list of producers
 * bound to a text input element.
 */

/**
 * @typedef {import('./producers.js').Producer} Producer
 */

/** @type {Producer[]} */
let items = [];
let highlightIdx = -1;
let inputEl = null;
let listEl = null;
let onSelectCallback = null;

/**
 * Initialise the dropdown.
 *
 * @param {HTMLInputElement} input    — The search text input
 * @param {HTMLElement}      dropdown — The dropdown list container
 * @param {(name: string) => void} onSelect — Called when a producer is selected
 */
export function initDropdown(input, dropdown, onSelect) {
  inputEl = input;
  listEl = dropdown;
  onSelectCallback = onSelect;

  inputEl.addEventListener('focus', open);
  inputEl.addEventListener('input', filter);
  inputEl.addEventListener('blur', () => setTimeout(close, 200));
  inputEl.addEventListener('keydown', handleKeydown);

  document.addEventListener('click', (e) => {
    if (!inputEl.parentElement?.contains(e.target)) {
      close();
    }
  });
}

/**
 * Update the dropdown item list (call after fetching producers).
 * @param {Producer[]} producers
 */
export function setItems(producers) {
  items = producers;
  render(items);
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

function open() {
  listEl.classList.add('open');
  filter();
}

function close() {
  listEl.classList.remove('open');
  highlightIdx = -1;
}

function filter() {
  const query = inputEl.value.trim().toLowerCase();
  const filtered = query ? items.filter((p) => p.name.includes(query)) : items;
  highlightIdx = -1;
  render(filtered);
}

function render(list) {
  if (list.length === 0) {
    listEl.innerHTML = '<div class="dropdown-empty">No matching producers</div>';
    return;
  }

  listEl.innerHTML = list
    .map(
      (p, i) => `
    <div class="dropdown-item ${i === highlightIdx ? 'highlighted' : ''}"
         data-name="${p.name}">
      <span>${p.name}</span>
      <span class="bp-rank ${p.isTop21 ? 'top21' : ''}">${
        p.isTop21
          ? 'Top 21 #' + p.rank
          : p.isActive
            ? 'Standby #' + p.rank
            : 'Inactive'
      }</span>
    </div>`
    )
    .join('');

  // Attach click listeners
  listEl.querySelectorAll('.dropdown-item').forEach((el) => {
    el.addEventListener('mousedown', () => selectItem(el.dataset.name));
  });
}

function selectItem(name) {
  inputEl.value = name;
  close();
  if (onSelectCallback) onSelectCallback(name);
}

function handleKeydown(e) {
  const items = listEl.querySelectorAll('.dropdown-item');

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    highlightIdx = Math.min(highlightIdx + 1, items.length - 1);
    updateHighlight(items);
    items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    highlightIdx = Math.max(highlightIdx - 1, 0);
    updateHighlight(items);
    items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
  } else if (e.key === 'Enter') {
    e.preventDefault();
    if (highlightIdx >= 0 && items[highlightIdx]) {
      selectItem(items[highlightIdx].dataset.name);
    } else if (items.length === 1) {
      selectItem(items[0].dataset.name);
    }
  } else if (e.key === 'Escape') {
    close();
    inputEl.blur();
  }
}

function updateHighlight(nodeList) {
  nodeList.forEach((el, i) => {
    el.classList.toggle('highlighted', i === highlightIdx);
  });
}
